import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, catchError, filter, finalize, forkJoin, from, map, Observable, of, race, Subscription, switchMap, take, tap, timer } from 'rxjs';
import { HxAuthService, HxCallcenterService, HxUserService, NotifierService } from 'hx-services';
import { AsteriskCallEvent, AsteriskNotifyService } from '@callcenter-app/service/asterisk-notify.service';
import { PushNotificationsService } from '@callcenter-app/service/push-notification.service';
import { CurrentOrderRow, OrdersCurrentService } from '@callcenter-app/service/orders-current.service';
import { cartLink, EditorNavigation, orderEditLink } from '@callcenter-app/pages/order-editor/order-editor.routes';
import { formatPhone } from '@callcenter-app/pages/orders-archive/orders-archive.format';
import { uiLabelText } from '@callcenter-app/util/ui-label';
import {
  CallAction,
  callOf,
  CallShellState,
  EditorOpen,
  INITIAL_CALL_STATE,
  LiveCart,
  nextCartEndOf,
  nextSyncOf,
  orderOpen,
  reduce,
  ShellCall
} from './call-state.machine';
import { editorRouteOf } from './call-editor-route';
import { isOldWorkplaceUrl, isWorkplaceUrl, WORKPLACE_PATH } from './workplace.config';
import { WorkplaceService } from './workplace.service';

/** The monotonic clock of the shell (`performance.now()`); a spec gives its own. */
export const CALL_STATE_CLOCK = new InjectionToken<() => number>('cc.callState.clock', { providedIn: 'root', factory: () => () => performance.now() });

/** The auto-retry of a client that did not load: every five seconds («Повторим сами через 5 секунд»), twelve times, then the button only. */
export const CLIENT_RETRY_MS = 5000;
export const CLIENT_RETRY_LIMIT = 12;
/** The phone of a new client is looked up again twice — the exchange makes the client a moment after it pushes the call. */
export const CLIENT_NOT_FOUND_RETRY_MS = [1500, 4000];
/** The notification waits this long for the name of the client. */
export const NOTIFY_WAIT_MS = 2000;
/** The live reservations of the call are read this often while there are some. */
export const CARTS_POLL_MS = 30_000;
/** The bus is closed: the call is read from the server this often meanwhile. */
export const BUS_DOWN_POLL_MS = 15_000;
/**
 * The call the workplace is about, kept in the browser: its id, the number of the operator and the ids of its reservations — no phone, no name (W1 does not know which reservation the page made for the call:
 * the link is the page's). After a reload (or in another tab) the finished call is read again by it and goes on where it stopped while a reservation of it is alive (the owner, 24.10); «Закрыть звонок», a
 * booking that ran out, another number, signing out — the key goes.
 */
export const HELD_CALL_KEY = 'cc.workplace.heldCall';

interface HeldCallMark {
  /** The number of the operator. */
  s: string;
  /** The id of the call. */
  c: string;
  /** The ids of the reservations of the call. */
  k?: number[];
}

/** No more ids of reservations are kept than a call can sensibly hold. */
const HELD_CARTS_KEPT = 20;

/** The session is gone without the sign-out of the menu (401): the mark goes with it — the interceptor has no `CallStateService` to purge (it would be a cycle through `HttpClient`). */
export function forgetHeldCallMark(): void {
  try {
    localStorage.removeItem(HELD_CALL_KEY);
  } catch {
    // no storage: nothing was kept
  }
}

function parseHeldMark(raw: string | null | undefined): HeldCallMark | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    const mark = JSON.parse(raw) as Partial<HeldCallMark> | null;
    if (typeof mark?.s !== 'string' || typeof mark.c !== 'string' || mark.c.length === 0 || mark.c.length > 128) {
      return undefined;
    }
    const k = Array.isArray(mark.k) ? mark.k.filter((id): id is number => Number.isSafeInteger(id) && id > 0).slice(0, HELD_CARTS_KEPT) : [];
    return { s: mark.s, c: mark.c, k };
  } catch {
    return undefined;
  }
}

interface BusMessage {
  body?: Partial<AsteriskCallEvent>;
}

/**
 * The call of the operator in the shell of the application (stage 4, design §3.2): the one subscription to `operator.{number}`, the order and the dedup of the events (the pure `reduce`), the answers of W1 / W2, the reservations of the
 * call, the editor the operator is in, the notification of the computer. Whoever needs the call — the band under the header, the workplace, the header itself — reads `state$`; nothing here ever moves the operator to another page.
 *
 * The seams of the next increments: `state$` carries `pending` (a call that waits for the order of the held one) and `editor` (the order that is open); `openOrderTarget()` is where «К заказу …» leads; `refreshCarts()` re-reads the
 * reservations of the call (the editor calls it when it makes or releases one); `noteEditor()` is for an editor page that knows better than the address.
 */
@Injectable({ providedIn: 'root' })
export class CallStateService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<CallShellState>(INITIAL_CALL_STATE);
  readonly state$: Observable<CallShellState> = this.stateSubject.asObservable();

  private started = false;
  private subs = new Subscription();
  private syncTimer?: ReturnType<typeof setTimeout>;
  private cartEndTimer?: ReturnType<typeof setTimeout>;
  private cartPollTimer?: ReturnType<typeof setInterval>;
  private busDownTimer?: ReturnType<typeof setInterval>;
  private clientTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private syncing = new Set<string>();
  /** The calls whose count of orders («15 заказов») is being read; `again` — an order was made / cancelled meanwhile, so the answer on its way is out of date and the count is read once more. */
  private totalsReading = new Set<string>();
  private totalsAgain = new Set<string>();
  private notified = new Set<string>();
  private currentUrl = '';
  /** The editor was entered from the workplace (or while the call was alive): every page of it, step 1 included, belongs to this call. */
  private flowCallId?: string;
  /** The operator closed the order of the held call by himself and the waiting call took its place (B2′) while the editor page is still on the screen: that page is the old call's, not the new one's, until it is left. */
  private flowSealed = false;
  private lastHeldId?: string;
  /** The finished call of the last page was looked for (once a page: the start of the number). */
  private resumeTried = false;

  /** One function for ever: the guard of `NotifierService.registerHandler` against a second registration compares it by reference. */
  private readonly onBusMessage = (error: unknown, message: BusMessage | undefined): void => {
    if (error) {
      console.error('error: ', error);
      return;
    }
    const body = message?.body;
    if (!body?.clientPhone) {
      // a call without a number is not shown anywhere (hidden numbers are out of the stage)
      return;
    }
    this.zone.run(() => {
      // the old page (`/call-center-old`) is fed as it always was
      this.asteriskNotify.notify(body as AsteriskCallEvent);
      this.dispatch({ type: 'event', event: body as AsteriskCallEvent });
    });
  };

  constructor(
    private notifier: NotifierService,
    private asteriskNotify: AsteriskNotifyService,
    private auth: HxAuthService,
    private userService: HxUserService,
    private callcenter: HxCallcenterService,
    private workplace: WorkplaceService,
    private current: OrdersCurrentService,
    private router: Router,
    private push: PushNotificationsService,
    private tr: TranslocoService,
    private zone: NgZone,
    @Inject(CALL_STATE_CLOCK) private clock: () => number,
  ) {
  }

  get snapshot(): CallShellState {
    return this.stateSubject.value;
  }

  /** From `HomeComponent`, once. */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.currentUrl = this.router.url;
    this.dispatch({ type: 'slot', slot: this.auth.user?.asteriskId || undefined });
    this.callcenter.getOperatorIds().then(ids => this.dispatch({ type: 'slots', available: ids.length > 0 }), () => undefined);

    // every opening of the bus: the handler is registered again (the client of the bus drops the handlers with its socket) and the call is read from the server (it may have come while the bus was down)
    this.subs.add(this.notifier.eventBusOpenObs.subscribe(bus => {
      if (bus) {
        this.stopBusDownPoll();
        this.dispatch({ type: 'bus', open: true });
        this.register();
        this.restore();
      } else {
        this.dispatch({ type: 'bus', open: false });
        this.startBusDownPoll();
      }
    }));

    this.subs.add(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => this.onNavigated(e.urlAfterRedirects)));
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('storage', this.onStorage);
    this.applyRoute(this.currentUrl, '', false);
  }

  /** The operator picked a number: no reload — the old handler goes, the new one comes, the calls of the new number are read. Errors (`operatorSlot.busy/forbidden`) reach the caller. */
  changeSlot(operatorId: number): Observable<void> {
    return this.userService.setOperatorId(operatorId).pipe(
      switchMap(() => from(this.auth.getIdentity()).pipe(catchError(() => of(undefined)))),
      tap(user => {
        this.auth.setAuthUser(user ?? { ...this.auth.user, asteriskId: String(operatorId) });
        this.applySlot(user?.asteriskId || String(operatorId));
      }),
      map(() => undefined),
    );
  }

  /** «Закрыть звонок» of a finished call that has no order open. */
  closeCall(): void {
    this.dispatch({ type: 'close' });
  }

  /** «Скрыть» of the band of a finished call. */
  hideBand(callId: string): void {
    this.dispatch({ type: 'hide', callId });
  }

  /** W2 again for a call (the button «Повторить», a saved name). */
  retryClient(callId: string): void {
    const call = callOf(this.snapshot, callId);
    if (!call) {
      return;
    }
    this.clearClientTimer(callId);
    this.dispatch({ type: 'client', callId, client: { status: 'loading' } });
    this.loadClient(callId, 0, true);
  }

  /** The call is read from the server now (entering the workplace, the tab coming back). */
  refresh(): void {
    if (this.snapshot.slot) {
      this.restore();
    }
    const held = this.snapshot.held;
    if (held) {
      this.syncCall(held.callId);
      this.refreshCarts(held.callId);
      // the count of the client's orders was read when the call came: an order may have been made since (F8)
      this.refreshOrdersTotal(held.callId);
    }
  }

  /**
   * F8: the count of the client's orders, which the card of the call and the band print («15 заказов»), is read again — it was read once, when the call came, and an order made or cancelled since moved it. The reads are the ones the
   * panel of the workplace makes (`clientSummary` + `clientCurrent`, kept for a few seconds), so the two always print the same number. `fresh`: what is kept about the client is out of date at once (an order was made) — it is dropped
   * first, and the panel that reads right after shares these reads. No timer: it is asked when the page is entered, the tab comes back, an order is made or cancelled.
   */
  refreshOrdersTotal(callId?: string, fresh = false): void {
    const id = callId ?? this.snapshot.held?.callId;
    const client = id ? callOf(this.snapshot, id)?.client : undefined;
    const clientId = client?.status === 'found' ? client.lookup.client?.id : undefined;
    if (!id || clientId === undefined) {
      return;
    }
    if (this.totalsReading.has(id)) {
      if (fresh) {
        this.totalsAgain.add(id);
      }
      return;
    }
    if (fresh) {
      this.workplace.forgetClient(clientId);
    }
    this.loadOrdersTotal(id, clientId);
  }

  /** The panel of the workplace printed «Заказов N» for this client: the held call prints the same N (F8). Anything but the client of the held call is none of its business. */
  noteOrdersTotal(clientId: number, total: number): void {
    const held = this.snapshot.held;
    const client = held?.client;
    if (held && client?.status === 'found' && client.lookup.client?.id === clientId && client.ordersTotal !== total) {
      this.dispatch({ type: 'client', callId: held.callId, client: { ...client, ordersTotal: total } });
    }
  }

  /** The reservations of the call are read again (`/orders-current/rows`): the editor made or released one, the page left the editor. */
  refreshCarts(callId?: string): void {
    const id = callId ?? this.snapshot.held?.callId;
    const call = id ? callOf(this.snapshot, id) : undefined;
    if (!id || !call) {
      return;
    }
    if (call.carts.length === 0) {
      return;
    }
    this.current.getRows([...call.carts]).subscribe({
      next: rows => this.dispatch({ type: 'carts', callId: id, live: this.liveOf(rows) }),
      error: () => undefined,
    });
  }

  /** The reservations of the operator that are alive, with the moment each runs out. */
  private liveOf(rows: CurrentOrderRow[]): LiveCart[] {
    const now = this.clock();
    return rows
      .filter(row => row.current.phase === 'CART' && row.current.cart?.mine && (row.current.cart.secondsLeft ?? 0) > 0)
      .map(row => ({
        id: row.id,
        until: now + (row.current.cart?.secondsLeft ?? 0) * 1000,
        title: uiLabelText(row.items[0]?.title, this.tr.getActiveLang()),
      }));
  }

  /** An editor page that knows the call better than the address does (the next increments): the order of the held call is open / closed. */
  noteEditor(editor?: EditorOpen): void {
    this.dispatch({ type: 'editor', editor });
  }

  /** Where «К заказу …» leads: the order that is open for the held call — its reservation (step 2) or its change. */
  openOrderTarget(): EditorNavigation | undefined {
    const state = this.snapshot;
    const held = state.held;
    if (!held) {
      return undefined;
    }
    const editor = state.editor;
    if (editor?.kind === 'edit' && editor.orderId !== undefined) {
      return orderEditLink(editor.orderId, { backLink: WORKPLACE_PATH });
    }
    const cart = [...held.liveCarts].sort((a, b) => b.until - a.until)[0];
    const cartId = cart?.id ?? editor?.cartId;
    return cartId !== undefined ? cartLink(cartId, { backLink: WORKPLACE_PATH }) : undefined;
  }

  /**
   * B2′: the operator closed an order of the held call by himself — «Создать заказ» (`created`), «Снять бронь» (`released`) — and a call that waits for it opens, though the conversation of the held one goes on (the reducer decides whether the order is
   * the held call's). The editor page that is still on the screen stays the old call's: it is not given to the new call.
   */
  orderClosed(orderId: number): void {
    const before = this.snapshot;
    const sealedBefore = this.flowSealed;
    // the held call changes INSIDE the dispatch (and the address is read again for the new call there): the seal goes on first
    const mayChange = !!before.held && !!before.pending && !!editorRouteOf(this.currentUrl);
    if (mayChange) {
      this.flowSealed = true;
    }
    this.dispatch({ type: 'closed', orderId });
    if (mayChange && this.snapshot.held?.callId === before.held?.callId) {
      this.flowSealed = sealedBefore;
    }
  }

  /** An order is open for the held call at this moment (see `orderOpen`). */
  orderIsOpen(): boolean {
    return orderOpen(this.snapshot, this.clock());
  }

  /** Sign-out: nothing of the session stays. */
  purge(): void {
    this.clearTimers();
    this.notified.clear();
    this.workplace.clearCache();
    this.flowCallId = undefined;
    this.flowSealed = false;
    this.lastHeldId = undefined;
    this.resumeTried = true;
    this.rememberHeld(undefined);
    this.stateSubject.next({ ...INITIAL_CALL_STATE, slotsAvailable: this.snapshot.slotsAvailable });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('storage', this.onStorage);
    this.clearTimers();
  }

  // ---------- the bus ----------

  private register(): void {
    const slot = this.snapshot.slot;
    if (slot) {
      this.notifier.registerHandler(`operator.${slot}`, this.onBusMessage);
    }
  }

  private applySlot(slot: string): void {
    const old = this.snapshot.slot;
    const busOpen = this.snapshot.busOpen;
    if (old && old !== slot && busOpen) {
      this.notifier.unregisterHandler(`operator.${old}`, this.onBusMessage);
    }
    this.clearCallTimers();
    this.dispatch({ type: 'slot', slot });
    if (busOpen) {
      this.register();
    }
    this.restore();
  }

  /**
   * W1 without an id: the call the number has right now. The first time a page asks it, the finished call the workplace was about before the reload comes first (W1 by its id + its reservations): it goes on
   * if its booking is alive, and a call that rings meanwhile waits behind it as it would have.
   */
  private restore(): void {
    const slot = this.snapshot.slot;
    if (!slot) {
      return;
    }
    const mark = this.resumeMarkOnce(slot);
    if (mark === undefined) {
      this.readCurrent();
      return;
    }
    const resumeId = mark.c;
    this.workplace.getCall(resumeId).pipe(
      switchMap(response => {
        const wc = response.call;
        const cartIds = [...new Set([...(wc?.cartIds ?? []), ...(mark.k ?? [])])];
        if (!wc || wc.state !== 'finished' || wc.elsewhere || cartIds.length === 0) {
          return of(undefined);
        }
        // the kept ids only ask: the call holds what W1 names and what is alive and the operator's own (a kept id is not trusted)
        return this.current.getRows(cartIds).pipe(map(rows => ({ call: wc, live: this.liveOf(rows) })));
      }),
      catchError(() => of(undefined)),
    ).subscribe(found => {
      if (found && found.live.length > 0 && this.snapshot.slot === slot) {
        this.dispatch({ type: 'resumed', call: found.call, live: found.live });
      }
      if (this.snapshot.held?.callId !== resumeId) {
        // nothing to go on with (the booking is gone, the call is not the operator's): the mark goes
        this.rememberHeld(this.snapshot.held);
      }
      this.readCurrent();
    });
  }

  private readCurrent(): void {
    this.workplace.getCall().subscribe({
      next: response => this.dispatch({ type: 'current', call: response.call }),
      error: () => undefined,
    });
  }

  /** The call to go on with — once a page, for the number it was kept for, while the workplace holds nothing yet. */
  private resumeMarkOnce(slot: string): HeldCallMark | undefined {
    if (this.resumeTried) {
      return undefined;
    }
    this.resumeTried = true;
    if (this.snapshot.held) {
      return undefined;
    }
    let mark: HeldCallMark | undefined;
    try {
      mark = parseHeldMark(localStorage.getItem(HELD_CALL_KEY));
    } catch {
      mark = undefined;
    }
    return mark?.s === slot ? mark : undefined;
  }

  /** The call the workplace is about is kept for the next page with its reservations; none — the mark goes. */
  private rememberHeld(call: ShellCall | undefined): void {
    const slot = this.snapshot.slot;
    try {
      if (call && slot) {
        localStorage.setItem(HELD_CALL_KEY, JSON.stringify({ s: slot, c: call.callId, k: call.carts.slice(-HELD_CARTS_KEPT) } satisfies HeldCallMark));
      } else {
        localStorage.removeItem(HELD_CALL_KEY);
      }
    } catch {
      // no storage (a private window, blocked site data): the page works, only a reload starts over
    }
  }

  /** Another tab of the operator let the finished call go («Закрыть звонок», its booking ran out): this one lets it go too. */
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== HELD_CALL_KEY || event.newValue !== null) {
      return;
    }
    const gone = parseHeldMark(event.oldValue)?.c;
    const held = this.snapshot.held;
    if (gone && held?.phase === 'finished' && held.callId === gone) {
      this.zone.run(() => this.dispatch({ type: 'close' }));
    }
  };

  private startBusDownPoll(): void {
    this.stopBusDownPoll();
    this.busDownTimer = setInterval(() => this.restore(), BUS_DOWN_POLL_MS);
  }

  private stopBusDownPoll(): void {
    if (this.busDownTimer !== undefined) {
      clearInterval(this.busDownTimer);
      this.busDownTimer = undefined;
    }
  }

  private readonly onVisible = (): void => {
    if (!document.hidden && this.snapshot.slot) {
      this.refresh();
    }
  };

  // ---------- the state ----------

  private dispatch(action: CallAction): void {
    const before = this.snapshot;
    const after = reduce(before, action, this.clock());
    if (after !== before) {
      this.stateSubject.next(after);
      this.afterChange(before, after);
    }
  }

  /** What a new state asks for. */
  private afterChange(before: CallShellState, after: CallShellState): void {
    const known = new Set([before.held?.callId, before.pending?.callId, before.previous?.callId]);
    [after.held, after.pending].forEach(call => {
      if (call && !known.has(call.callId)) {
        this.onNewCall(call);
      }
    });
    const gone = [before.held, before.pending, before.previous].filter((c): c is ShellCall => !!c && !callOf(after, c.callId));
    gone.forEach(c => this.clearClientTimer(c.callId));

    if (after.held?.callId !== this.lastHeldId || (after.held && after.held.carts !== before.held?.carts)) {
      // the call the workplace is about, or the reservations it holds, changed: the mark for the next page follows
      this.rememberHeld(after.held);
    }
    if (after.held?.callId !== this.lastHeldId) {
      this.lastHeldId = after.held?.callId;
      // the call the page is open for changed: the address may name its order
      if (after.held && this.currentUrl) {
        this.applyRoute(this.currentUrl, '', false);
      }
    }
    this.armSync();
    this.armCartEnd();
    this.armCartPoll();
  }

  private onNewCall(call: ShellCall): void {
    this.loadClient(call.callId, 0, false);
    if (call.carts.length) {
      this.refreshCarts(call.callId);
    }
    this.notifyWhenReady(call.callId);
  }

  // ---------- W1 ----------

  private armSync(): void {
    if (this.syncTimer !== undefined) {
      clearTimeout(this.syncTimer);
      this.syncTimer = undefined;
    }
    const next = nextSyncOf(this.snapshot);
    if (!next) {
      return;
    }
    this.syncTimer = setTimeout(() => this.syncCall(next.callId), Math.max(0, next.at - this.clock()));
  }

  private syncCall(callId: string): void {
    if (!callOf(this.snapshot, callId) || this.syncing.has(callId)) {
      return;
    }
    this.syncing.add(callId);
    this.workplace.getCall(callId).subscribe({
      next: response => {
        this.syncing.delete(callId);
        const carts = callOf(this.snapshot, callId)?.carts.length ?? 0;
        this.dispatch({ type: 'server', callId, call: response.call });
        if ((callOf(this.snapshot, callId)?.carts.length ?? 0) !== carts) {
          this.refreshCarts(callId);
        }
      },
      error: () => {
        this.syncing.delete(callId);
        this.dispatch({ type: 'syncFailed', callId });
      },
    });
  }

  // ---------- W2 ----------

  private loadClient(callId: string, attempt: number, manual: boolean): void {
    const call = callOf(this.snapshot, callId);
    if (!call) {
      return;
    }
    this.workplace.lookupClient(call.phone).subscribe({
      next: response => {
        if (!callOf(this.snapshot, callId)) {
          return;
        }
        if (response.client) {
          this.dispatch({ type: 'client', callId, client: { status: 'found', lookup: response } });
          this.loadOrdersTotal(callId, response.client.id);
        } else if (attempt < CLIENT_NOT_FOUND_RETRY_MS.length && !manual) {
          // the exchange makes the client of a new number a moment after it pushed the call
          this.later(callId, CLIENT_NOT_FOUND_RETRY_MS[attempt], () => this.loadClient(callId, attempt + 1, false));
        } else {
          this.dispatch({ type: 'client', callId, client: { status: 'none', lookup: response } });
        }
      },
      error: () => {
        if (!callOf(this.snapshot, callId)) {
          return;
        }
        this.dispatch({ type: 'client', callId, client: { status: 'error' } });
        if (attempt < CLIENT_RETRY_LIMIT) {
          this.later(callId, CLIENT_RETRY_MS, () => {
            const still = callOf(this.snapshot, callId);
            if (still?.client.status === 'error') {
              this.loadClient(callId, attempt + 1, true);
            }
          });
        }
      },
    });
  }

  /** «15 заказов»: the finished ones of the summary of «Архива» and the ones in work. Both reads are kept for the page to use. */
  private loadOrdersTotal(callId: string, clientId: number): void {
    this.totalsReading.add(callId);
    forkJoin({
      summary: this.workplace.clientSummary(clientId),
      page: this.workplace.clientCurrent(clientId).pipe(catchError(() => of(undefined))),
    }).pipe(finalize(() => {
      this.totalsReading.delete(callId);
      if (this.totalsAgain.delete(callId)) {
        // an order was made while this was on its way: its answer may not know it
        this.refreshOrdersTotal(callId, true);
      }
    })).subscribe(({ summary, page }) => {
      const call = callOf(this.snapshot, callId);
      if (!call || call.client.status !== 'found' || (summary.status === 'error' && !page)) {
        return;
      }
      const done = summary.status === 'ok' ? summary.summary.stats.orders : 0;
      const inWork = page ? page.list.filter(row => row.current.phase === 'IN_WORK').length : 0;
      if (call.client.ordersTotal !== done + inWork) {
        this.dispatch({ type: 'client', callId, client: { status: 'found', lookup: call.client.lookup, ordersTotal: done + inWork } });
      }
    });
  }

  private later(callId: string, ms: number, run: () => void): void {
    this.clearClientTimer(callId);
    this.clientTimers.set(callId, setTimeout(() => {
      this.clientTimers.delete(callId);
      run();
    }, ms));
  }

  private clearClientTimer(callId: string): void {
    const timer = this.clientTimers.get(callId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.clientTimers.delete(callId);
    }
  }

  // ---------- the reservations ----------

  /** A reservation that runs out frees the order of the call: the state is asked to look again at that moment. */
  private armCartEnd(): void {
    if (this.cartEndTimer !== undefined) {
      clearTimeout(this.cartEndTimer);
      this.cartEndTimer = undefined;
    }
    const now = this.clock();
    const end = nextCartEndOf(this.snapshot, now);
    if (end !== undefined) {
      this.cartEndTimer = setTimeout(() => {
        this.cartEndTimer = undefined;
        this.dispatch({ type: 'tick' });
        // the server is asked whether the booking really ran out — its term is 10 minutes from the last action in the cart, which another tab may have moved
        this.refreshCarts();
        this.armCartEnd();
      }, end - now + 50);
    }
  }

  /** While the call holds a live reservation it is read now and then — a reservation made an order, or released in another tab, frees the order. */
  private armCartPoll(): void {
    const held = this.snapshot.held;
    const needed = !!held && held.liveCarts.length > 0;
    if (needed && this.cartPollTimer === undefined) {
      this.cartPollTimer = setInterval(() => this.refreshCarts(), CARTS_POLL_MS);
    } else if (!needed && this.cartPollTimer !== undefined) {
      clearInterval(this.cartPollTimer);
      this.cartPollTimer = undefined;
    }
  }

  // ---------- the router ----------

  /**
   * The rule «an order is open» needs to know where the operator is. Entering the editor FROM the workplace, or while the call is alive, makes the pages of the editor belong to the held call; step 2 names its reservation
   * in the address — it is remembered at once (S2); leaving the editor reads the reservations again.
   */
  private onNavigated(url: string): void {
    const previous = this.currentUrl;
    this.currentUrl = url;
    this.applyRoute(url, previous, !!editorRouteOf(previous));
  }

  private applyRoute(url: string, previous: string, leftEditor: boolean): void {
    const route = editorRouteOf(url);
    const state = this.snapshot;
    const held = state.held;

    if (!route) {
      this.flowCallId = undefined;
      this.flowSealed = false;
      const leaving = state.editor;
      if (leaving?.kind === 'edit' && leaving.orderId !== undefined && held && leaving.callId === held.callId) {
        // B2′: the change of an order of the held call is left — saved, cancelled, or left by the menu: the operator closed it himself, and a call that waits for it opens (the editor is still the held call's here)
        this.dispatch({ type: 'closed', orderId: leaving.orderId });
      }
      if (this.snapshot.editor) {
        this.dispatch({ type: 'editor', editor: undefined });
      }
      const now = this.snapshot.held;
      if (leftEditor && now && now.carts.length) {
        this.refreshCarts(now.callId);
      }
      if (leftEditor && now) {
        // an order may have been made or changed there (F8)
        this.refreshOrdersTotal(now.callId);
      }
      return;
    }
    if (!held || this.flowSealed) {
      return;
    }
    if (this.flowCallId !== held.callId && (isWorkplaceUrl(previous) || held.phase !== 'finished')) {
      this.flowCallId = held.callId;
    }
    const ours = this.flowCallId === held.callId || (route.kind === 'cart' && held.carts.includes(route.cartId));
    if (!ours) {
      return;
    }
    if (route.kind === 'choice') {
      // step 1 holds nothing; the draft stays where it is
      if (state.editor) {
        this.dispatch({ type: 'editor', editor: undefined });
        this.refreshCarts(held.callId);
      }
      return;
    }
    if (route.kind === 'cart') {
      this.dispatch({ type: 'cartSeen', callId: held.callId, cartId: route.cartId });
      this.dispatch({ type: 'editor', editor: { callId: held.callId, kind: 'cart', cartId: route.cartId } });
      return;
    }
    this.dispatch({ type: 'editor', editor: { callId: held.callId, kind: 'edit', orderId: route.orderId } });
  }

  // ---------- the notification ----------

  /** One notification per call: if the tab is hidden or has no focus, the page is not the old one (it makes its own), the browser allows it. */
  private notifyWhenReady(callId: string): void {
    if (this.notified.has(callId)) {
      return;
    }
    this.notified.add(callId);
    const clientReady$ = this.state$.pipe(
      map(state => callOf(state, callId)?.client.status),
      filter(status => status !== undefined && status !== 'loading'),
    );
    race(clientReady$, timer(NOTIFY_WAIT_MS)).pipe(take(1)).subscribe(() => this.notify(callId));
  }

  private notify(callId: string): void {
    const call = callOf(this.snapshot, callId);
    if (!call || call.phase === 'finished' || this.push.permission !== 'granted' || isOldWorkplaceUrl(this.router.url)) {
      return;
    }
    if (!document.hidden && document.hasFocus()) {
      return;
    }
    const phone = formatPhone(call.phone) ?? call.phone;
    const name = call.client.status === 'found' ? call.client.lookup.client?.name?.trim() : undefined;
    this.push.create(this.tr.translate('callBar.notify.title', { who: name || phone }), {
      body: phone,
      tag: `cc-call-${callId}`,
    }).subscribe({
      next: (result: { event?: { type?: string } } | undefined) => {
        if (result?.event?.type === 'click') {
          window.focus();
          void this.router.navigateByUrl(WORKPLACE_PATH);
        }
      },
      error: () => undefined,
    });
  }

  // ---------- the timers ----------

  private clearTimers(): void {
    this.clearCallTimers();
    this.stopBusDownPoll();
  }

  /** The timers that belong to the calls (not the one that watches the bus). */
  private clearCallTimers(): void {
    [this.syncTimer, this.cartEndTimer].forEach(timer => timer !== undefined && clearTimeout(timer));
    this.syncTimer = undefined;
    this.cartEndTimer = undefined;
    if (this.cartPollTimer !== undefined) {
      clearInterval(this.cartPollTimer);
      this.cartPollTimer = undefined;
    }
    this.clientTimers.forEach(timer => clearTimeout(timer));
    this.clientTimers.clear();
    this.syncing.clear();
    this.totalsReading.clear();
    this.totalsAgain.clear();
  }
}
